import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';

import Navbar from './Components/Navbar';
import Login from './Pages/Login';
import Dashboard from './Pages/Dashboard';
import Members from './Pages/Members';
import Loans from './Pages/Loans';
import Fines from './Pages/Fines';
import Rosca from './Pages/Rosca';
import Mifl from './Pages/Mifl';
import Reports from './Pages/Reports';
import Subscriptions from './Pages/Subscriptions';
import Savings from './Pages/Savings';
import Treasury from './Pages/Treasury';
import Expenses from './Pages/Expenses';
import FinancialDashboard from './Pages/FinancialDashboard';
import Secretariat from './Pages/Secretariat';
import Documents from './Pages/Documents';
import AuditLog from './Pages/AuditLog';
import UserManagement from './Pages/UserManagement';
import Payments from './Pages/Payments';
import Projections from './Pages/Projections';
import Gallery from './Pages/Gallery';
import MemberPortal from './Pages/MemberPortal';
import FinancialCycles from './Pages/FinancialCycles';
import SystemJobs from './Pages/SystemJobs';
import Notifications from './Pages/Notifications';

axios.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (!token) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

const StaffRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (!token) return <Navigate to="/login" replace />;
  if (!['admin', 'finance', 'secretary', 'chairman'].includes(user.role))
    // A member has no reason to land anywhere but their portal. Any other
    // role denied a full-staff page (e.g. Credit Officer hitting Fines)
    // isn't a member and has no portal data of their own — send them to
    // their actual home page instead.
    return <Navigate to={user.role === 'member' ? '/portal' : '/dashboard'} replace />;
  return children;
};

// Dashboard, Loans, and Treasury are the only modules a Credit Officer
// may use (plus /financial, which is already open to every authenticated
// role below) — everything else stays behind the full-staff StaffRoute.
const CreditRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (!token) return <Navigate to="/login" replace />;
  if (!['admin', 'finance', 'secretary', 'chairman', 'credit_officer'].includes(user.role))
    return <Navigate to={user.role === 'member' ? '/portal' : '/dashboard'} replace />;
  return children;
};

function AppContent() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));

  const handleLogin = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setIsAuthenticated(true);
    if (user.role === 'member') {
      navigate('/portal', { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    navigate('/login', { replace: true });
  };

  return (
    <>
      {isAuthenticated && <Navbar onLogout={handleLogout} />}
      <Routes>
        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} />} />

        {/* All authenticated — member + staff */}
        <Route path="/portal"           element={<ProtectedRoute><MemberPortal /></ProtectedRoute>} />
        <Route path="/gallery"          element={<ProtectedRoute><Gallery /></ProtectedRoute>} />
        <Route path="/financial"        element={<ProtectedRoute><FinancialDashboard /></ProtectedRoute>} />
        <Route path="/projections"      element={<ProtectedRoute><Projections /></ProtectedRoute>} />
        <Route path="/financial-cycles" element={<ProtectedRoute><FinancialCycles /></ProtectedRoute>} />

        {/* Staff only */}
        <Route path="/dashboard"     element={<CreditRoute><Dashboard /></CreditRoute>} />
        <Route path="/members"       element={<StaffRoute><Members /></StaffRoute>} />
        <Route path="/loans"         element={<CreditRoute><Loans /></CreditRoute>} />
        <Route path="/fines"         element={<StaffRoute><Fines /></StaffRoute>} />
        <Route path="/rosca"         element={<StaffRoute><Rosca /></StaffRoute>} />
        <Route path="/mifl"          element={<StaffRoute><Mifl /></StaffRoute>} />
        <Route path="/subscriptions" element={<StaffRoute><Subscriptions /></StaffRoute>} />
        <Route path="/savings"       element={<StaffRoute><Savings /></StaffRoute>} />
        <Route path="/treasury"      element={<CreditRoute><Treasury /></CreditRoute>} />
        <Route path="/expenses"      element={<StaffRoute><Expenses /></StaffRoute>} />
        <Route path="/secretariat"   element={<StaffRoute><Secretariat /></StaffRoute>} />
        <Route path="/documents"     element={<StaffRoute><Documents /></StaffRoute>} />
        <Route path="/reports"       element={<StaffRoute><Reports /></StaffRoute>} />
        <Route path="/audit"         element={<StaffRoute><AuditLog /></StaffRoute>} />
        <Route path="/payments"      element={<StaffRoute><Payments /></StaffRoute>} />
        <Route path="/notifications" element={<StaffRoute><Notifications /></StaffRoute>} />

        {/* Admin only */}
        <Route path="/users"         element={<AdminRoute><UserManagement /></AdminRoute>} />
        <Route path="/system-jobs"   element={<AdminRoute><SystemJobs /></AdminRoute>} />

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;